/**
 * Capture: turn an editor change into an edit of the shared text without
 * leaking the editor's normalisation (`__bold__` read back as `**bold**`, `~~~`
 * fences as ```, a loose list made tight, CRLF as LF...) into text the user did
 * not touch.
 *
 * `w` is the editor's serialisation at the last sync point, `wNext` its
 * serialisation now, `s` the shared text at the last sync point. Each changed
 * range of `diff(w, wNext)` is mapped into `s` through the code units `w` and
 * `s` share (the alignment below), anchored to the unchanged neighbour on each
 * side; everything else in `s` stays byte-identical. An insertion goes right
 * after its left neighbour when `s` has it, else right before its right
 * neighbour, stepped back over the `\r`, spaces and tabs `s` has there and `w`
 * does not (so text typed at the end of a line lands before a CRLF or trailing
 * spaces, not inside them); a deletion or replacement anchored on its right
 * neighbour stops before them the same way. A change whose edge sits inside a normalised region
 * (neither neighbour is in `s`) grows over that region, which then takes the
 * editor's form: deleting a `__` marker, typing between the two characters of a
 * `**` marker.
 *
 * The alignment of `w` and `s`, in three steps:
 *  1. Lines. Each line's key is the line without trailing `\r`, spaces and
 *     tabs; a diff over the keys pairs equal lines. Between two equal lines,
 *     a run of other lines pairs line by line when both sides have the same
 *     number of them, else the run is aligned as one segment (step 2).
 *  2. Content first. In a segment (a pair of lines, or a run), a diff with the
 *     markdown punctuation `*_-~\`+#>|=` masked on both sides (it can never
 *     match) aligns letters, digits, other punctuation and whitespace. A
 *     bullet's `-` can then never be taken for a bold's `*`, nor a rule's
 *     `***` for a closing `**`.
 *  3. Then the markup. Between two anchors of step 2, a plain diff aligns what
 *     is left, but a retain of at most 3 code units made only of that
 *     punctuation or whitespace, with a change on both sides of it, is dropped
 *     (a stray `*` shared by `- *` and `* _` is no anchor).
 * A line pair or segment that is byte-identical maps one to one. Each diff is
 * capped at an edit distance of 2000 (`diff`); past it a segment has no
 * anchors inside, and an edit there takes the editor's form for that whole
 * segment (the documented fallback). The line step keeps big notes cheap: a
 * CRLF note's lines all pair by key.
 *
 * `captureEditChecked` adds the caller's safety net, and `CaptureCache` reuses
 * the alignment while `s` changes only through the user's own edits.
 *
 * Reference implementation for the Swift and Kotlin ports, which run the same
 * `captureMapping.fixtures.json`. Change all three or none.
 */

import { diff } from "./textOps"

interface Hunk {
  a: number
  b: number
}

/** Markdown punctuation an editor rewrites (`__` for `**`, `*` bullets for `-`, `~~~` for ```...). */
const MARKUP = "*_-~`+#>|="
const W_MASK = ""
const S_MASK = ""
/** The longest markup-or-whitespace retain step 3 drops between two changes. */
const MAX_DROPPED_RETAIN = 3
/** Distinct line keys a line diff can tell apart (BMP code units outside the surrogates). */
const MAX_LINE_KEYS = 0xd800 + 0x2000

const isMarkup = (ch: string) => MARKUP.includes(ch)
const isSpace = (ch: string) => ch === " " || ch === "\t" || ch === "\r" || ch === "\n"

/**
 * `at[i]`: where code unit `i` of `w` sits in `s`, -1 when `s` does not have
 * it. Increasing wherever it is not -1, and `w[i] === s[at[i]]`.
 */
export function alignCapture(w: string, s: string): Int32Array {
  const at = new Int32Array(w.length).fill(-1)
  if (w === s) {
    for (let i = 0; i < w.length; i++) at[i] = i
    return at
  }
  const wl = lineStarts(w)
  const sl = lineStarts(s)
  const keys = new Map<string, string>()
  const token = (text: string, starts: number[]) => {
    let out = ""
    for (let k = 0; k + 1 < starts.length; k++) {
      const key = text.slice(starts[k], lineEnd(text, starts, k)).replace(/[ \t\r]+$/, "")
      let t = keys.get(key)
      if (t === undefined) {
        const id = keys.size
        t = String.fromCharCode(id < 0xd800 ? id : id + 0x800)
        keys.set(key, t)
      }
      out += t
    }
    return out
  }
  const wt = token(w, wl)
  const st = token(s, sl)
  if (keys.size > MAX_LINE_KEYS) {
    alignSegment(w, 0, w.length, s, 0, s.length, at)
    return at
  }

  const pairLine = (i: number, j: number) => {
    alignSegment(w, wl[i], lineEnd(w, wl, i), s, sl[j], lineEnd(s, sl, j), at)
    // Both lines end in "\n": the terminators pair.
    if (wl[i + 1] <= w.length && sl[j + 1] <= s.length) at[wl[i + 1] - 1] = sl[j + 1] - 1
  }
  let i = 0
  let j = 0
  let gapI = 0
  let gapJ = 0
  const flush = () => {
    const dw = i - gapI
    const ds = j - gapJ
    if (dw > 0 && dw === ds) {
      for (let k = 0; k < dw; k++) pairLine(gapI + k, gapJ + k)
    } else if (dw > 0 && ds > 0) {
      alignSegment(w, wl[gapI], Math.min(wl[i], w.length), s, sl[gapJ], Math.min(sl[j], s.length), at)
    }
  }
  for (const c of diff(wt, st)) {
    if (typeof c === "string") j += c.length
    else if (c < 0) i -= c
    else {
      flush()
      for (let k = 0; k < c; k++) pairLine(i + k, j + k)
      i += c
      j += c
      gapI = i
      gapJ = j
    }
  }
  flush()
  return at
}

/** Start of each line, then `text.length + 1` (as if the last line had a terminator). */
function lineStarts(text: string): number[] {
  const out = [0]
  for (let k = text.indexOf("\n"); k >= 0; k = text.indexOf("\n", k + 1)) out.push(k + 1)
  out.push(text.length + 1)
  return out
}

/** End of line `k`'s content (its "\n" excluded). */
function lineEnd(text: string, starts: number[], k: number): number {
  return Math.min(starts[k + 1] - 1, text.length)
}

/** Steps 2 and 3 of the alignment over `w[wa, wb)` and `s[sa, sb)`. */
function alignSegment(w: string, wa: number, wb: number, s: string, sa: number, sb: number, at: Int32Array): void {
  if (wa >= wb || sa >= sb) return
  const ws = w.slice(wa, wb)
  const ss = s.slice(sa, sb)
  if (ws === ss) {
    for (let k = 0; k < ws.length; k++) at[wa + k] = sa + k
    return
  }
  // Step 2: markup masked, a different mask per side so it never matches.
  const mask = (text: string, m: string, other: string) => {
    let out = ""
    for (const ch of text) out += ch.length === 1 && (isMarkup(ch) || ch === other) ? m : ch
    return out
  }
  let i = 0
  let j = 0
  let gapI = 0
  let gapJ = 0
  for (const c of diff(mask(ws, W_MASK, S_MASK), mask(ss, S_MASK, W_MASK))) {
    if (typeof c === "string") j += c.length
    else if (c < 0) i -= c
    else {
      alignMarkup(w, wa + gapI, wa + i, s, sa + gapJ, sa + j, at)
      for (let k = 0; k < c; k++) at[wa + i + k] = sa + j + k
      i += c
      j += c
      gapI = i
      gapJ = j
    }
  }
  alignMarkup(w, wa + gapI, wa + i, s, sa + gapJ, sa + j, at)
}

/** Step 3 over `w[wa, wb)` and `s[sa, sb)`, between two content anchors. */
function alignMarkup(w: string, wa: number, wb: number, s: string, sa: number, sb: number, at: Int32Array): void {
  if (wa >= wb || sa >= sb) return
  const ss = s.slice(sa, sb)
  const ops = diff(w.slice(wa, wb), ss)
  let i = wa
  let j = sa
  for (let k = 0; k < ops.length; k++) {
    const c = ops[k]
    if (typeof c === "string") j += c.length
    else if (c < 0) i -= c
    else {
      const between = k > 0 && k < ops.length - 1
      const text = ss.slice(j - sa, j - sa + c)
      const stray = between && c <= MAX_DROPPED_RETAIN && [...text].every((ch) => isMarkup(ch) || isSpace(ch))
      if (!stray) for (let n = 0; n < c; n++) at[i + n] = j + n
      i += c
      j += c
    }
  }
}

export function captureEdit(w: string, wNext: string, s: string): string {
  if (w === wNext) return s
  return captureWith(w, wNext, s, alignCapture(w, s)).text
}

/**
 * `captureEdit` with the caller's safety net. `roundTrip` is the editor's
 * `serialise(parse(text))`. When the captured text does not read back the way
 * the editor's own text does, the mapping put something where the user did not
 * type it: the editor's text is taken instead (its normalisation leaks into
 * the whole note once, nothing is misplaced). Comparing against
 * `roundTrip(wNext)` rather than `wNext` keeps a serialiser that is not quite
 * idempotent from failing every capture. Each platform wires its own
 * serialiser; the round trip runs only when the capture differs from `wNext`.
 */
export function captureEditChecked(w: string, wNext: string, s: string, roundTrip: (text: string) => string): string {
  return checked(captureEdit(w, wNext, s), wNext, roundTrip)
}

function checked(out: string, wNext: string, roundTrip: (text: string) => string): string {
  if (out === wNext) return out
  return roundTrip(out) === roundTrip(wNext) ? out : wNext
}

/**
 * The alignment, kept between captures while `s` changes only through them:
 * a capture whose `w` and `s` are the previous capture's `wNext` and result
 * reuses the alignment carried through that capture instead of diffing the
 * whole note again (about 64 ms a capture on a big CRLF note before the line
 * step). Any other `w` or `s` (a remote change moved the shared text, the
 * editor reloaded) aligns afresh. One per open editor.
 */
export class CaptureCache {
  private w: string | null = null
  private s: string | null = null
  private at: Int32Array | null = null

  capture(w: string, wNext: string, s: string, roundTrip?: (text: string) => string): string {
    if (w === wNext) return s
    const at = this.at !== null && w === this.w && s === this.s ? this.at : alignCapture(w, s)
    const r = captureWith(w, wNext, s, at)
    const out = roundTrip ? checked(r.text, wNext, roundTrip) : r.text
    if (out === r.text) {
      this.w = wNext
      this.s = out
      this.at = r.at
    } else this.clear()
    return out
  }

  clear(): void {
    this.w = this.s = null
    this.at = null
  }
}

/** The capture over a given alignment, and the alignment of `wNext` with the result. */
function captureWith(w: string, wNext: string, s: string, at: Int32Array): { text: string; at: Int32Array } {
  // The changed ranges of w, with their length change.
  const changes: { a: number; b: number; delta: number }[] = []
  let i = 0
  for (const c of diff(w, wNext)) {
    if (typeof c === "number" && c > 0) {
      i += c
      continue
    }
    let last = changes[changes.length - 1]
    if (!last || last.b !== i) {
      last = { a: i, b: i, delta: 0 }
      changes.push(last)
    }
    if (typeof c === "string") last.delta += c.length
    else {
      last.b -= c
      last.delta += c
      i -= c
    }
  }

  const matched = new Uint8Array(s.length)
  for (let k = 0; k < at.length; k++) if (at[k] >= 0) matched[at[k]] = 1
  const kept = (p: number) => at[p] >= 0
  const start = (h: Hunk) =>
    h.a === 0 ? 0 : kept(h.a - 1) ? at[h.a - 1] + 1 : h.a < h.b && kept(h.a) ? at[h.a] : -1
  const end = (h: Hunk) =>
    h.b === w.length ? s.length : kept(h.b) ? at[h.b] : h.a < h.b && kept(h.b - 1) ? at[h.b - 1] + 1 : -1
  /** Back over the `\r`, spaces and tabs before `p` that w does not have, never below `floor`. */
  const stepBack = (p: number, floor: number) => {
    while (p > floor && !matched[p - 1] && (s[p - 1] === "\r" || s[p - 1] === " " || s[p - 1] === "\t")) p--
    return p
  }

  // Grow a range until both edges have an anchor in s; returns its range in s.
  const settle = (h: Hunk, floor: number): [number, number] => {
    for (;;) {
      const s0 = start(h)
      const s1 = end(h)
      if (h.a === h.b) {
        if (s0 >= 0) return [s0, s0]
        if (s1 >= 0) {
          const p = stepBack(s1, floor)
          return [p, p]
        }
        h.a--
        continue
      }
      // The right edge anchored on a neighbour (or the end) of w: the `\r`,
      // spaces and tabs s has before it and w does not stay.
      if (s0 >= 0 && s1 >= 0) return [s0, h.b === w.length || kept(h.b) ? stepBack(s1, s0) : s1]
      if (s0 < 0) h.a--
      if (s1 < 0) h.b++
    }
  }

  const out: { h: Hunk; s0: number; s1: number }[] = []
  for (const c of changes) {
    const h = { a: c.a, b: c.b }
    const floor = () => (out.length > 0 ? out[out.length - 1].s1 : 0)
    let [s0, s1] = settle(h, floor())
    // Ranges that meet or overlap in w become one.
    while (out.length > 0 && h.a <= out[out.length - 1].h.b) {
      const prev = out.pop()!
      h.a = Math.min(h.a, prev.h.a)
      h.b = Math.max(h.b, prev.h.b)
      ;[s0, s1] = settle(h, floor())
    }
    out.push({ h, s0, s1 })
  }

  // Positions in wNext: w outside the changes is unchanged, shifted by the
  // changes before it. A merged range never starts or ends inside a change.
  const shift = (p: number, inclusive: boolean) =>
    changes.reduce((sum, c) => (c.a < p || (inclusive && c.a === p) ? sum + c.delta : sum), p)

  // The result, and the alignment of wNext with it: w outside the ranges
  // keeps its place in s (moved with the text around it), and what a range
  // takes from wNext is the result's own text.
  const atNext = new Int32Array(wNext.length).fill(-1)
  let ci = 0
  let delta = 0
  let wCursor = 0
  const carry = (from: number, to: number, sCursor: number, base: number) => {
    for (let p = from; p < to; p++) {
      while (ci < changes.length && changes[ci].a <= p) delta += changes[ci++].delta
      if (at[p] >= 0) atNext[p + delta] = base + at[p] - sCursor
    }
  }
  let result = ""
  let cursor = 0
  for (const { h, s0, s1 } of out) {
    carry(wCursor, h.a, cursor, result.length)
    result += s.slice(cursor, s0)
    const n0 = shift(h.a, false)
    const n1 = shift(h.b, true)
    for (let k = n0; k < n1; k++) atNext[k] = result.length + k - n0
    result += wNext.slice(n0, n1)
    cursor = s1
    wCursor = h.b
  }
  carry(wCursor, w.length, cursor, result.length)
  return { text: result + s.slice(cursor), at: atNext }
}
